/**
 * Web Push subscription helper (B15, CF-2404).
 *
 * Registers the service worker, requests Notification permission, and creates
 * / removes a PushManager subscription bound to our self-hosted VAPID key.
 */

import { getVapidPublicKey, pushSubscribe, pushUnsubscribe } from '../../api/notificationsApi';
import { log } from '../logger';

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission(): NotificationPermission {
  return isPushSupported() ? Notification.permission : 'denied';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

/** Register SW early (idempotent) so push events can be received out-of-tab. */
export async function registerServiceWorker(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    await ensureServiceWorker();
  } catch (err) {
    log.warn('sw_register_failed', { err: String(err) });
  }
}

/** Returns true on success. Requests permission + subscribes + posts to server. */
export async function enablePush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  try {
    const reg = await ensureServiceWorker();
    const vapid = await getVapidPublicKey();
    if (vapid === '') return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
    await pushSubscribe(sub.toJSON());
    return true;
  } catch (err) {
    log.warn('push_enable_failed', { err: String(err) });
    return false;
  }
}

/** Removes the active subscription locally + server-side. */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await pushUnsubscribe(sub.endpoint);
      await sub.unsubscribe();
    }
  } catch (err) {
    log.warn('push_disable_failed', { err: String(err) });
  }
}

export async function hasActiveSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  return sub != null;
}
